/**
 * PersonalResidenceScenarios — Phase 48 HERO surface.
 *
 * Three side-by-side wealth-comparison columns for personal-
 * residence deals. Winning column gets an emerald highlight,
 * trophy icon, and a "+X% more wealth" badge; below the trio a
 * single-sentence AI verdict tells the user *why* it won.
 *
 * The data comes verbatim from `deal.scenarios_comparison`, which
 * the backend `_compute_scenarios` builds off the same 5-year
 * appreciation, Section 121, and (optionally) Florida homestead
 * math the rest of the page uses. This component is the primary
 * decision-support surface for owner-occupant deals.
 */
import { Home, Key, Hammer, Trophy } from "lucide-react";

const ICONS = {
  live: Home,
  rent: Key,
  flip: Hammer,
};

const ACCENT = {
  live: { text: "text-teal-700", bg: "bg-teal-50", ring: "ring-teal-500", winBg: "bg-teal-100" },
  rent: { text: "text-indigo-700", bg: "bg-indigo-50", ring: "ring-indigo-500", winBg: "bg-indigo-100" },
  flip: { text: "text-orange-700", bg: "bg-orange-50", ring: "ring-orange-500", winBg: "bg-orange-100" },
};

function fmtMoney(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(Number(n));
  if (abs >= 1_000_000) return `$${(Number(n) / 1_000_000).toFixed(2)}M`;
  return `$${Math.round(Number(n)).toLocaleString("en-US")}`;
}

export default function PersonalResidenceScenarios({ scenarios }) {
  if (!scenarios || !Array.isArray(scenarios.scenarios) || scenarios.scenarios.length === 0) {
    return null;
  }
  const winnerId = scenarios.winner;
  const delta = scenarios.wealth_delta_pct ?? 0;
  const reasoning = scenarios.winner_reasoning || "";
  return (
    <section
      data-testid="personal-residence-scenarios"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <header className="mb-5 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-baseline">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Alternative Scenarios · 5-year wealth
          </p>
          <h3 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
            Which path builds more wealth?
          </h3>
        </div>
        <p className="max-w-md text-xs text-slate-500 sm:text-right">
          Same house, three plans. Numbers are 5-year net wealth including
          equity build, tax benefits, and (in FL) homestead savings.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {scenarios.scenarios.map((s) => {
          const isWinner = s.id === winnerId;
          const Icon = ICONS[s.id] || Home;
          const a = ACCENT[s.id] || ACCENT.live;
          const wealth = s.net_wealth_5yr;
          return (
            <div
              key={s.id}
              data-testid={`pr-scenario-${s.id}`}
              data-winner={isWinner ? "true" : "false"}
              className={[
                "relative flex flex-col rounded-xl border p-4",
                isWinner
                  ? `${a.winBg} border-transparent shadow-md ring-2 ring-offset-2 ${a.ring}`
                  : "border-slate-200 bg-slate-50/40",
              ].join(" ")}
            >
              {isWinner ? (
                <div
                  data-testid={`pr-scenario-${s.id}-winner-badge`}
                  className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow-sm"
                >
                  <Trophy className="h-3 w-3" />
                  Winner{delta ? ` · +${Math.abs(delta).toFixed(0)}% wealth` : ""}
                </div>
              ) : null}
              <div className="mb-3 flex items-center gap-2">
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${a.bg} ${a.text}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className={`text-sm font-bold ${isWinner ? "text-slate-900" : "text-slate-700"}`}>
                    {s.label}
                  </p>
                </div>
              </div>
              <div className="mb-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  5-year net wealth
                </p>
                <p
                  data-testid={`pr-scenario-${s.id}-wealth`}
                  className="mt-0.5 text-2xl font-bold tabular-nums text-slate-900"
                >
                  {fmtMoney(wealth)}
                </p>
              </div>
              <div className="mt-1 flex-1 space-y-1 text-xs text-slate-600">
                {s.equity_end != null ? (
                  <p className="flex justify-between">
                    <span>Equity built</span>
                    <span className="tabular-nums font-medium">{fmtMoney(s.equity_end)}</span>
                  </p>
                ) : null}
                {s.tax_benefits_cumulative != null ? (
                  <p className="flex justify-between">
                    <span>Tax benefits</span>
                    <span className="tabular-nums font-medium">{fmtMoney(s.tax_benefits_cumulative)}</span>
                  </p>
                ) : null}
                {s.cash_flow_after_conversion_monthly != null ? (
                  <p className="flex justify-between">
                    <span>Rental CF after yr 2</span>
                    <span className="tabular-nums font-medium">{fmtMoney(s.cash_flow_after_conversion_monthly)}/mo</span>
                  </p>
                ) : null}
                {s.gross_profit != null ? (
                  <p className="flex justify-between">
                    <span>Gross gain @ yr 2</span>
                    <span className="tabular-nums font-medium">{fmtMoney(s.gross_profit)}</span>
                  </p>
                ) : null}
                {s.tax_savings_from_exclusion != null && s.tax_savings_from_exclusion > 0 ? (
                  <p
                    data-testid={`pr-scenario-${s.id}-section121-badge`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
                  >
                    ↳ Sec 121 saves {fmtMoney(s.tax_savings_from_exclusion)}
                  </p>
                ) : null}
              </div>
              <p className="mt-3 border-t border-slate-200 pt-2 text-[11px] leading-snug text-slate-500">
                {s.notes}
              </p>
            </div>
          );
        })}
      </div>

      {reasoning ? (
        <div
          data-testid="pr-scenario-verdict-card"
          className="mt-5 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 px-5 py-4 text-white shadow-md"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
            AI verdict
          </p>
          <p
            className="mt-1 text-sm leading-relaxed"
            /* Verdict copy uses **markdown-style** bolding, so we
                strip the asterisks and render the first sentence
                with a stronger weight to draw the eye. */
          >
            {renderReasoning(reasoning)}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function renderReasoning(raw) {
  // Convert the leading "**Live in it.**" pattern to a real <strong>
  const boldRe = /\*\*(.+?)\*\*/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = boldRe.exec(raw)) !== null) {
    if (m.index > lastIdx) {
      parts.push(raw.slice(lastIdx, m.index));
    }
    parts.push(<strong key={`b-${i++}`} className="font-bold text-white">{m[1]}</strong>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < raw.length) parts.push(raw.slice(lastIdx));
  return parts;
}
